import { Component, inject, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  SiApplicationHeaderComponent,
  SiHeaderAccountItemComponent,
  SiHeaderActionItemComponent,
  SiHeaderActionsDirective,
  SiHeaderBrandDirective,
  SiHeaderCollapsibleActionsComponent,
  SiHeaderSiemensLogoComponent
} from '@siemens/element-ng/application-header';
import {
  SiHeaderDropdownComponent,
  SiHeaderDropdownItemComponent,
  SiHeaderDropdownTriggerDirective
} from '@siemens/element-ng/header-dropdown';
import { NavbarVerticalItem, SiNavbarVerticalComponent } from '@siemens/element-ng/navbar-vertical';
import { ThemeType } from '@siemens/element-ng/theme';

import { SettingsService } from '../../services/settings.service';
import { AuthService } from '../../services/auth.service';
import { ChatService } from 'src/app/services/chat.service';
import { MainThread } from 'src/app/interfaces/api-response.interface';
import { tap } from 'rxjs/internal/operators/tap';
import { catchError } from 'rxjs/internal/operators/catchError';
import { of } from 'rxjs/internal/observable/of';

@Component({
  selector: 'app-main',
  imports: [
    CommonModule,
    RouterOutlet,
    SiApplicationHeaderComponent,
    SiHeaderDropdownComponent,
    SiHeaderDropdownItemComponent,
    SiHeaderDropdownTriggerDirective,
    SiHeaderSiemensLogoComponent,
    SiHeaderCollapsibleActionsComponent,
    SiHeaderBrandDirective,
    SiHeaderAccountItemComponent,
    SiHeaderActionItemComponent,
    SiHeaderActionsDirective,
    SiNavbarVerticalComponent,
    RouterLink,
    TranslateModule
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss',
  host: {
    class: 'has-navbar-fixed-top d-block'
  }
})
export class MainComponent implements OnInit {

  private readonly settingsService = inject(SettingsService);
  private readonly authService = inject(AuthService);
  private readonly chatService = inject(ChatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  showLanguage = false;
  showTheme = false;
  showProfile = false;

  allThreads: unknown;
  userName: string = '';
  userEmail: string = '';
  userRole: string = 'User';

  ngOnInit(): void {
    this.loadUserInfo();
    this.getAllThreads();
    this.getThreadWhenQueryAsked();
  }

  protected readonly languageItems = this.settingsService.availableLanguages.map(language => ({
    value: language,
    label: `LANGUAGE.${language.toUpperCase()}`
  }));

  menuItems: NavbarVerticalItem[] = [
    {
      type: 'router-link',
      label: 'Gridscale X™ PSS®E Copilot',
      id: 'home',
      icon: 'element-ai',
      routerLink: 'main/chat-enhanced'
    },
    { type: 'divider' },
    {
      type: 'router-link',
      label: 'New Chat',
      id: 'new_chat',
      icon: 'element-plus',
      routerLink: 'main/chat-enhanced',
    }
  ];

  switchLanguage(language: string) {
    this.settingsService.setLanguage(language);
  }

  switchTheme(theme: string) {
    this.settingsService.setTheme(theme as ThemeType);
  }

  getTheme(): ThemeType {
    return this.settingsService.theme;
  }

  getLanguage(): string {
    return this.settingsService.language;
  }

  getUserName(): string {
    return (this.userName && this.userName.trim().length > 0) ? this.userName : 'User';
  }

  getUserEmail(): string {
    return (this.userEmail && this.userEmail.trim().length > 0) ? this.userEmail : 'No email available';
  }

  getUserRole(): string {
    return (this.userRole && this.userRole.trim().length > 0) ? this.userRole : 'User';
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  async loadUserInfo(): Promise<void> {
    try {
      const userProfile = await this.getUserProfile();
      if (userProfile) {
        this.updateUserProfile(userProfile);
        return;
      }
      const tokenClaims = this.getTokenClaims();
      if (tokenClaims) {
        this.updateTokenClaims(tokenClaims);
        return;
      }
      this.updateDefaultUser();
    } catch (error) {
      // Error loading user info - fall back to default user values
      this.updateDefaultUser();
    }
  }

  // Try cached profile; otherwise load it. Returns undefined if nothing available.
  private async getUserProfile(): Promise<unknown | undefined> {
    const cached = this.authService.getUserProfile();
    if (cached) return cached;
    const loaded = await this.authService.loadUserProfile();
    return loaded ?? undefined;
  }

  private updateUserProfile(userProfile: unknown): void {
    const up = userProfile as Record<string, unknown> | undefined;
    const hasFullName = up && up['firstName'] && up['lastName'];
    this.userName = hasFullName
      ? `${up['firstName']} ${up['lastName']}`
      : (up?.['username'] as string ?? 'User');
    this.userEmail = (up?.['email'] as string) ?? '';

    const roles = this.authService.getUserRoles();
    this.userRole = roles.length > 0 ? roles[0] : 'User';
  }

  private getTokenClaims(): unknown | undefined {
    const keycloakInstance = this.authService['keycloakService'].getKeycloakInstance();
    return keycloakInstance?.tokenParsed ?? undefined;
  }

  private updateTokenClaims(claims: unknown): void {
    const c = claims as Record<string, unknown> | undefined;
    this.userName = (c?.['name'] as string) ?? (c?.['preferred_username'] as string) ?? 'User';
    this.userEmail = (c?.['email'] as string) ?? '';
  }

  private updateDefaultUser(): void {
    this.userName = 'User';
    this.userEmail = '';
    if (!this.userRole) this.userRole = 'User';
  }

  getAllThreads() {
    this.chatService.getThreadsList().pipe(
      tap(response => {

        const chatHistoryIndex = this.menuItems.findIndex(item => 'id' in item && item.id === 'chat-history');
        if (chatHistoryIndex !== -1) {
          this.menuItems.splice(chatHistoryIndex, 1);
        }


        const res = response.threads;
        this.allThreads = res;
        const chatHistoryItem = res.map((thread: MainThread) => ({
          type: 'router-link',
          label: `${thread.thread_name}`,
          routerLink: `chat-enhanced/${thread.thread_id}`
        }));

        this.ngZone.run(() => {
          setTimeout(() => {
            this.menuItems = [
              ...this.menuItems,
              {
                type: 'group',
                label: 'Chat History',
                id: 'chat-history',
                icon: 'element-clock',
                children: chatHistoryItem
              }
            ];

            this.cdr.detectChanges();
          }, 100);
        });

      }),
      catchError(error => {
        return of(null);
      })
    ).subscribe();
  }

  getThreadWhenQueryAsked() {
    this.chatService.getSharedThreadId().subscribe(
      (response) => {
        if (response) {

          this.getAllThreads();
        }
      }
    );
  }
}
